import { Monitor, Moon, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { IconButton } from '../IconButton';
import { SegmentedControl, type SegmentedOption } from '../SegmentedControl';
import { useTheme } from './themeContext';
import { isThemePreference, type ThemePreference } from './themeStorage';

const OPTIONS: ReadonlyArray<SegmentedOption<ThemePreference>> = [
  { value: 'system', label: 'System', icon: <Monitor />, hideLabel: true },
  { value: 'light', label: 'Light', icon: <Sun />, hideLabel: true },
  { value: 'dark', label: 'Dark', icon: <Moon />, hideLabel: true },
];

export interface ThemeToggleProps {
  /** `menu`: icon button with a menu (header). `segmented`: three icons inline. */
  variant?: 'menu' | 'segmented';
  className?: string;
}

/** System / Light / Dark switch for the header or a settings panel. */
export const ThemeToggle = ({
  variant = 'menu',
  className,
}: ThemeToggleProps) => {
  const { theme, resolvedTheme, setTheme } = useTheme();

  if (variant === 'segmented') {
    return (
      <SegmentedControl
        aria-label="Theme"
        size="sm"
        value={theme}
        onValueChange={setTheme}
        options={OPTIONS}
        className={className}
      />
    );
  }

  const current =
    OPTIONS.find((option) => option.value === theme) ?? OPTIONS[0];
  const TriggerIcon = resolvedTheme === 'dark' ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          aria-label={`Theme: ${current.label}`}
          tooltip="Theme"
          icon={<TriggerIcon />}
          className={className}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (isThemePreference(value)) setTheme(value);
          }}
        >
          {OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              icon={option.icon}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
